import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { itemOf, messageOf, readLink, resetWithCode, resetWithLink } from '../api';
import type { TLink } from '../api';
import { SIGN_IN_PATHS } from '../constants';
import { updateFlow } from '../flow';
import { passwordOk, passwordRules } from '../rules';
import { Actions, BigButton, Field, Frame, Messages, Rules, Title } from '../ui';
import { useLive, useOutcome } from '../useOutcome';

/** Loads a reset or invite link; null while it loads, and a link with valid false when it can't be read. */
export const useLink = (token: string | null) => {
  const [link, setLink] = useState<TLink | null>(null);
  useEffect(() => {
    let live = true;
    if (token) {
      readLink(token).then((answer) => {
        if (live) {
          setLink(itemOf(answer) || ({ valid: false } as TLink));
        }
      });
    }
    return () => {
      live = false;
    };
  }, [token]);
  return link;
};

const PasswordForm = ({ email, saveLabel, onSave }: {
  email: string;
  saveLabel: string;
  onSave: (password: string) => Promise<string | null>;
}) => {
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useLive();
  const mismatch = !!second && first !== second;
  const ready = passwordOk(first, email) && first === second;
  const toggle = { shown, onToggle: () => setShown((now) => !now) };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) {
      return;
    }
    setBusy(true);
    setError('');
    const message = await onSave(first);
    if (live.current) {
      setBusy(false);
      if (message) {
        setError(message);
      }
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <Field id="sx-new-password" label="New password" value={first} onChange={setFirst} autoComplete="new-password" password={toggle} />
      <Field
        id="sx-new-password-again"
        label="Type it again"
        value={second}
        onChange={setSecond}
        autoComplete="new-password"
        password={toggle}
        bad={mismatch}
        hint={mismatch ? 'The passwords don’t match.' : undefined}
      />
      <Rules rules={passwordRules(first, email)} />
      <Messages error={error} />
      <Actions>
        <BigButton type="submit" disabled={!ready} aria-busy={busy}>{saveLabel}</BigButton>
      </Actions>
    </form>
  );
};

const Sub = ({ email }: { email: string }) => <>For {email}. You’ll be signed out on other devices.</>;

/** A reset link from the new sign-in (/new-password?token=...): saving signs in here and out everywhere else. */
const ResetWithLink = ({ token }: { token: string }) => {
  const link = useLink(token);
  const outcome = useOutcome();

  if (!link) {
    return <Frame />;
  }
  if (!link.valid) {
    return <Redirect to={SIGN_IN_PATHS.EXPIRED} />;
  }
  if (link.purpose === 'coach-invite' || link.purpose === 'team-invite') {
    return <Redirect to={`${SIGN_IN_PATHS.INVITE}?token=${encodeURIComponent(token)}`} />;
  }
  const email = link.email || '';

  return (
    <Frame>
      <Title title="Choose a new password" sub={<Sub email={email} />} />
      <PasswordForm
        email={email}
        saveLabel="Save password and sign in"
        onSave={async (newPassword) => outcome(await resetWithLink({ token, newPassword, keepSignedIn: true }), { keepSignedIn: true, email })}
      />
    </Frame>
  );
};

/** An older reset link (?code=&email=, still sent by the apps): saves through Account/reset-password, then sign in. */
const ResetWithCode = ({ code, email }: { code: string; email: string }) => {
  const history = useHistory();
  return (
    <Frame>
      <Title title="Choose a new password" sub={<Sub email={email} />} />
      <PasswordForm
        email={email}
        saveLabel="Save password"
        onSave={async (newPassword) => {
          const answer = await resetWithCode({ email, code, newPassword });
          // postData answers an error with its message, and success with the envelope.
          if (typeof answer !== 'object' || !answer || answer.success === false) {
            return messageOf(answer);
          }
          updateFlow({ email, notice: 'Your password is saved. Sign in with it.' });
          history.push(SIGN_IN_PATHS.SIGN_IN);
          return null;
        }}
      />
    </Frame>
  );
};

/** /new-password while the new sign-in is on (and always for ?token= links). */
export const ResetScreen = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get('token');
  if (token) {
    return <ResetWithLink token={token} />;
  }
  const code = params.get('code');
  // The current page did the same: a "+" in the address arrives as a space.
  const email = (params.get('email') || '').replace(/ /g, '+');
  if (code && email) {
    return <ResetWithCode code={code} email={email} />;
  }
  return <Redirect to={SIGN_IN_PATHS.FORGOT} />;
};
